import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import userDataService from '../services/userDataService';

const UserDataContext = createContext();

export const useUserData = () => {
  const context = useContext(UserDataContext);
  if (!context) {
    throw new Error('useUserData must be used within a UserDataProvider');
  }
  return context;
};

export const UserDataProvider = ({ children }) => {
  const { user } = useAuth();
  const [userSettings, setUserSettings] = useState(null);
  const [userPrompts, setUserPrompts] = useState([]);
  const [userFeeds, setUserFeeds] = useState([]);
  const [userApiKeys, setUserApiKeys] = useState({});
  const [loading, setLoading] = useState(true);

  // Load user data when user changes
  useEffect(() => {
    const loadUserData = async () => {
      console.log('UserDataContext: Loading user data for user:', user?.id);
      
      if (!user?.id) {
        console.log('UserDataContext: No user ID, clearing data');
        setUserSettings(null);
        setUserPrompts([]);
        setUserFeeds([]);
        setUserApiKeys({});
        setLoading(false);
        return;
      }

      try {
        console.log('UserDataContext: Starting to load user data...');
        setLoading(true);
        
        // Load all user data in parallel
        const [settings, prompts, feeds, apiKeys] = await Promise.all([
          userDataService.getUserSettings(user.id),
          userDataService.getUserPrompts(user.id),
          userDataService.getUserFeeds(user.id),
          userDataService.getUserApiKeys(user.id)
        ]);

        console.log('UserDataContext: Loaded data:', { settings, prompts, feeds, apiKeys });
        
        setUserSettings(settings);
        
        // Provide defaults if user has no custom prompts/feeds
        const finalPrompts = prompts.length === 0 ? userDataService.getDefaultPrompts() : prompts;
        const finalFeeds = feeds.length === 0 ? userDataService.getDefaultFeeds() : feeds;
        
        setUserPrompts(finalPrompts);
        setUserFeeds(finalFeeds);
        setUserApiKeys(apiKeys);
      } catch (error) {
        console.error('UserDataContext: Error loading user data:', error);
        // Set default values on error
        setUserSettings(userDataService.getDefaultUserSettings());
        setUserPrompts(userDataService.getDefaultPrompts());
        setUserFeeds(userDataService.getDefaultFeeds());
        setUserApiKeys({
          openaiApiKey: '',
          finnhubApiKey: process.env.REACT_APP_FINNHUB_API_KEY || '',
          finnhubEnabled: true
        });
      } finally {
        setLoading(false);
        console.log('UserDataContext: Finished loading user data');
      }
    };

    loadUserData();
  }, [user?.id]);

  // USER SETTINGS
  const updateUserSettings = async (newSettings) => {
    if (!user?.id) return false;
    
    try {
      const success = await userDataService.saveUserSettings(user.id, newSettings);
      if (success) {
        setUserSettings(prev => ({ ...prev, ...newSettings }));
      }
      return success;
    } catch (error) {
      console.error('Error updating user settings:', error);
      return false;
    }
  };

  // USER PROMPTS
  const addUserPrompt = async (prompt) => {
    if (!user?.id) return null;
    
    try {
      const promptId = await userDataService.saveUserPrompt(user.id, prompt);
      if (promptId) {
        const newPrompt = { ...prompt, id: promptId };
        setUserPrompts(prev => [...prev, newPrompt]);
        return promptId;
      }
      return null;
    } catch (error) {
      console.error('Error adding user prompt:', error);
      return null;
    }
  };

  const updateUserPrompt = async (promptId, updates) => {
    if (!user?.id) return false;
    
    try {
      const success = await userDataService.updateUserPrompt(user.id, promptId, updates);
      if (success) {
        setUserPrompts(prev => 
          prev.map(prompt => 
            prompt.id === promptId ? { ...prompt, ...updates } : prompt
          )
        );
      }
      return success;
    } catch (error) {
      console.error('Error updating user prompt:', error);
      return false;
    }
  };

  const deleteUserPrompt = async (promptId) => {
    if (!user?.id) return false;
    
    try {
      const success = await userDataService.deleteUserPrompt(user.id, promptId);
      if (success) {
        setUserPrompts(prev => prev.filter(prompt => prompt.id !== promptId));
      }
      return success;
    } catch (error) {
      console.error('Error deleting user prompt:', error);
      return false;
    }
  };

  // USER FEEDS
  const addUserFeed = async (feed) => {
    if (!user?.id) return null;
    
    try {
      const feedId = await userDataService.saveUserFeed(user.id, feed);
      if (feedId) {
        const newFeed = { ...feed, id: feedId };
        setUserFeeds(prev => [...prev, newFeed]);
        return feedId;
      }
      return null;
    } catch (error) {
      console.error('Error adding user feed:', error);
      return null;
    }
  };

  const updateUserFeed = async (feedId, updates) => {
    if (!user?.id) return false;
    
    try {
      const success = await userDataService.updateUserFeed(user.id, feedId, updates);
      if (success) {
        setUserFeeds(prev => 
          prev.map(feed => 
            feed.id === feedId ? { ...feed, ...updates } : feed
          )
        );
      }
      return success;
    } catch (error) {
      console.error('Error updating user feed:', error);
      return false;
    }
  };

  const deleteUserFeed = async (feedId) => {
    if (!user?.id) return false;
    
    try {
      const success = await userDataService.deleteUserFeed(user.id, feedId);
      if (success) {
        setUserFeeds(prev => prev.filter(feed => feed.id !== feedId));
      }
      return success;
    } catch (error) {
      console.error('Error deleting user feed:', error);
      return false;
    }
  };

  // USER API KEYS (only for non-MANAGED users, except Reddit which is allowed for all)
  const updateUserApiKeys = async (newApiKeys) => {
    if (!user?.id) return false;
    
    // For Reddit settings, allow all users to save
    const isRedditOnlyUpdate = Object.keys(newApiKeys).every(key => 
      key === 'redditEnabled' || key === 'redditApiKey'
    );
    
    // Don't allow MANAGED users to save their own API keys (except Reddit)
    if (!isRedditOnlyUpdate && (user.subscriptionType === 'MANAGED' || user.role === 'ADMIN')) {
      return false;
    }
    
    try {
      const success = await userDataService.saveUserApiKeys(user.id, newApiKeys);
      if (success) {
        setUserApiKeys(prev => ({ ...prev, ...newApiKeys }));
      }
      return success;
    } catch (error) {
      console.error('Error updating user API keys:', error);
      return false;
    }
  };

  // Get effective API keys based on user subscription
  const getEffectiveApiKeys = () => {
    console.log('getEffectiveApiKeys called with user:', user, 'userApiKeys:', userApiKeys);
    
    if (!user) {
      console.log('getEffectiveApiKeys: No user, returning defaults');
      return { 
        openaiApiKey: '', 
        finnhubApiKey: process.env.REACT_APP_FINNHUB_API_KEY || '', 
        finnhubEnabled: false,
        yahooFinanceEnabled: true,
        redditEnabled: false,
        redditApiKey: ''
      };
    }
    
    // MANAGED users and ADMIN users use system keys
    if (user.subscriptionType === 'MANAGED' || user.role === 'ADMIN') {
      console.log('getEffectiveApiKeys: MANAGED/ADMIN user, using system keys');
      return {
        openaiApiKey: '', // Will be handled by App.js with defaultManagedKey
        finnhubApiKey: process.env.REACT_APP_FINNHUB_API_KEY || '', // System FinHub key
        finnhubEnabled: userApiKeys?.finnhubEnabled || false,
        yahooFinanceEnabled: userApiKeys?.yahooFinanceEnabled !== false,
        redditEnabled: userApiKeys?.redditEnabled || false,
        redditApiKey: userApiKeys?.redditApiKey || ''
      };
    }
    
    // EARLY_BIRD users use their own keys
    console.log('getEffectiveApiKeys: EARLY_BIRD user, using user keys');
    return {
      openaiApiKey: userApiKeys?.openaiApiKey || '',
      finnhubApiKey: userApiKeys?.finnhubApiKey || process.env.REACT_APP_FINNHUB_API_KEY || '',
      finnhubEnabled: userApiKeys?.finnhubEnabled || false,
      yahooFinanceEnabled: userApiKeys?.yahooFinanceEnabled !== false,
      redditEnabled: userApiKeys?.redditEnabled || false,
      redditApiKey: userApiKeys?.redditApiKey || ''
    };
  };

  // Utility function to reset to defaults (useful for clearing duplicates)
  const resetToDefaults = async () => {
    if (!user?.id) return false;
    
    try {
      // Clear all user prompts and feeds from Firebase
      await userDataService.clearUserData(user.id);
      
      // Reload user data (which will now show defaults)
      const [settings, , , apiKeys] = await Promise.all([
        userDataService.getUserSettings(user.id),
        userDataService.getUserPrompts(user.id),
        userDataService.getUserFeeds(user.id),
        userDataService.getUserApiKeys(user.id)
      ]);

      setUserSettings(settings);
      setUserPrompts(userDataService.getDefaultPrompts());
      setUserFeeds(userDataService.getDefaultFeeds());
      setUserApiKeys(apiKeys);
      
      return true;
    } catch (error) {
      console.error('Error resetting to defaults:', error);
      return false;
    }
  };

  const value = {
    // State
    userSettings,
    userPrompts,
    userFeeds,
    userApiKeys,
    loading,
    
    // Settings methods
    updateUserSettings,
    
    // Prompt methods
    addUserPrompt,
    updateUserPrompt,
    deleteUserPrompt,
    
    // Feed methods
    addUserFeed,
    updateUserFeed,
    deleteUserFeed,
    
    // API Key methods
    updateUserApiKeys,
    getEffectiveApiKeys,
    
    // Utility methods
    resetToDefaults
  };

  return (
    <UserDataContext.Provider value={value}>
      {children}
    </UserDataContext.Provider>
  );
};
